import React, { useEffect, useState } from 'react';
import { List } from 'lucide-react';

const toId = (text, index) => {
  const base = (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
  return base || `section-${index + 1}`;
};

/**
 * Adds anchor ids to the article's h2/h3 tags and returns the outline.
 * Done on the HTML string (not the live DOM) so React never strips the ids
 * and the pre-rendered HTML ships with working anchors.
 */
export const buildOutline = (html) => {
  if (!html || typeof DOMParser === 'undefined') return { html: html || '', outline: [] };

  const doc = new DOMParser().parseFromString(`<div id="kb-body">${html}</div>`, 'text/html');
  const root = doc.getElementById('kb-body');
  const seen = new Set();
  const outline = [...root.querySelectorAll('h2, h3')]
    .filter((el) => el.textContent.trim())
    .map((el, index) => {
      let id = toId(el.textContent, index);
      while (seen.has(id)) id = `${id}-${index}`;
      seen.add(id);
      el.id = id;
      el.classList.add('scroll-mt-28');
      return { id, text: el.textContent.trim(), level: el.tagName === 'H3' ? 3 : 2 };
    });

  return { html: root.innerHTML, outline };
};

/** Tracks which heading is in view and scrolls to a heading on request. */
export const useOutlineSpy = (outline) => {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    if (!outline.length) return undefined;
    const elements = outline.map((h) => document.getElementById(h.id)).filter(Boolean);
    if (!elements.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          setActiveId(visible[0].target.id);
          return;
        }
        // between headings: keep the last one the reader scrolled past
        const passed = elements.filter((el) => el.getBoundingClientRect().top < 140).pop();
        if (passed) setActiveId(passed.id);
      },
      { rootMargin: '-120px 0px -65% 0px', threshold: [0, 1] }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [outline]);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
    window.history.replaceState(null, '', `#${id}`);
  };

  return { activeId, goTo };
};

const OutlineList = ({ items, activeId, goTo, testIdPrefix }) => (
  <ul className="space-y-0.5 border-l border-stone-200">
    {items.map((heading, index) => {
      const active = heading.id === activeId;
      return (
        <li key={heading.id}>
          <button
            type="button"
            onClick={() => goTo(heading.id)}
            className={`-ml-px block w-full border-l-2 py-1.5 pr-2 text-left text-sm leading-snug transition-colors ${
              heading.level === 3 ? 'pl-7' : 'pl-4'
            } ${
              active
                ? 'border-[#006A71] bg-teal-50/70 font-semibold text-[#006A71]'
                : 'border-transparent text-stone-600 hover:border-stone-300 hover:text-stone-900'
            }`}
            data-testid={`${testIdPrefix}-${index}`}
          >
            {heading.text}
          </button>
        </li>
      );
    })}
  </ul>
);

export const ArticleOutline = ({ items, activeId, goTo, footer }) => {
  if (!items.length) return null;
  return (
    <nav aria-label="Article outline" data-testid="article-toc-sidebar">
      <p className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#006A71]">
        <List className="h-4 w-4" aria-hidden="true" />
        In this article
      </p>
      <OutlineList items={items} activeId={activeId} goTo={goTo} testIdPrefix="article-toc-item" />
      {footer}
    </nav>
  );
};

export const ArticleOutlineMobile = ({ items, activeId, goTo }) => {
  if (!items.length) return null;
  return (
    <details
      className="mb-8 rounded-xl border border-stone-200 bg-stone-50 p-4 lg:hidden"
      data-testid="article-mobile-toc"
    >
      <summary className="flex cursor-pointer items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#006A71]">
        <List className="h-4 w-4" aria-hidden="true" />
        In this article
        <span className="ml-auto text-[11px] font-medium normal-case tracking-normal text-stone-500">
          {items.length} sections
        </span>
      </summary>
      <div className="mt-3">
        <OutlineList items={items} activeId={activeId} goTo={goTo} testIdPrefix="article-mobile-toc-item" />
      </div>
    </details>
  );
};
